import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MdDeleteForever,
  MdWarning,
  MdCloudUpload,
  MdContentCopy,
  MdCheckCircle,
  MdLogin,
} from 'react-icons/md';
import { authenticatedFetch } from '../../lib/api';
import { useToast } from '../../components/shared/Toast';
import {
  erpPageBreadcrumbClass,
  erpPageBreadcrumbCurrentClass,
  erpPageBreadcrumbItemClass,
  erpPageBreadcrumbSepClass,
  PageSkeleton,
} from '@euroasiann/shared/ui';

const CLEAN_DATA_CONFIRM_PHRASE = 'CLEAN ALL DATA';

interface PortalCredential {
  portal: string;
  organization: string;
  name: string;
  email: string;
  password: string;
  role: string;
  loginHint: string;
}

interface CleanDataStats {
  organizations: number;
  users: number;
  roles: number;
  customerOnboardings: number;
  vendorOnboardings: number;
}

export function DataManagementPage() {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [cleanConfirm, setCleanConfirm] = useState('');
  const [showCleanModal, setShowCleanModal] = useState(false);
  const [cleanStats, setCleanStats] = useState<CleanDataStats | null>(null);
  const [seedSummary, setSeedSummary] = useState<{
    created: string[];
    updated: string[];
    errors?: string[];
  } | null>(null);
  const [copiedEmail, setCopiedEmail] = useState<string | null>(null);

  const { data: credentials = [], isLoading } = useQuery<PortalCredential[]>({
    queryKey: ['mock-data-credentials'],
    queryFn: async () => {
      const res = await authenticatedFetch('/api/v1/admin/mock-data/credentials');
      if (!res.ok) return [];
      const json = await res.json();
      return json.data ?? [];
    },
  });

  const seedMutation = useMutation({
    mutationFn: async () => {
      const res = await authenticatedFetch('/api/v1/admin/mock-data/seed', { method: 'POST' });
      const json = await res.json();
      if (!res.ok && res.status !== 207) throw new Error(json.error || 'Failed to seed demo data');
      return json.data;
    },
    onSuccess: (data) => {
      const warnings = data.errors as string[] | undefined;
      setSeedSummary({
        created: data.stats?.created ?? [],
        updated: data.stats?.updated ?? [],
        errors: warnings,
      });
      queryClient.invalidateQueries({ queryKey: ['mock-data-credentials'] });
      if (warnings?.length) {
        showToast(`Demo seed finished with ${warnings.length} warning(s)`, 'warning');
      } else {
        showToast('Demo environment seeded successfully', 'success');
      }
    },
    onError: (err: Error) => showToast(err.message, 'error'),
  });

  const cleanMutation = useMutation({
    mutationFn: async (confirmPhrase: string) => {
      const res = await authenticatedFetch('/api/v1/admin/clean-data', {
        method: 'POST',
        body: JSON.stringify({ confirmPhrase }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to clean data');
      return json.data as CleanDataStats;
    },
    onSuccess: (stats) => {
      setCleanStats(stats);
      setCleanConfirm('');
      setShowCleanModal(false);
      queryClient.invalidateQueries();
      showToast('Customer and vendor data removed', 'success');
    },
    onError: (err: Error) => showToast(err.message, 'error'),
  });

  const copyCredential = async (cred: PortalCredential) => {
    const text = `${cred.portal}\nEmail: ${cred.email}\nPassword: ${cred.password}`;
    await navigator.clipboard.writeText(text);
    setCopiedEmail(cred.email);
    setTimeout(() => setCopiedEmail(null), 2000);
  };

  return (
    <div className="w-full min-w-0">
      <div className="mb-6">
        <nav className={`${erpPageBreadcrumbClass} mb-2`} aria-label="Breadcrumb">
          <span className={erpPageBreadcrumbItemClass}>Admin</span>
          <span className={erpPageBreadcrumbSepClass}>/</span>
          <span className={erpPageBreadcrumbCurrentClass}>Data Management</span>
        </nav>
        <h1 className="text-3xl font-bold text-[hsl(var(--foreground))]">Data Management</h1>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">
          Seed a professional demo environment across Tech, Admin, Customer, Vendor, and Port Agent
          portals, or wipe customer/vendor operational data for a fresh start.
        </p>
      </div>

      {/* Add Mock Data */}
      <div className="mb-8 p-6 rounded-xl border border-[#0088cc]/30 bg-[#0088cc]/5 dark:bg-[#0088cc]/10">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
          <div>
            <h2 className="text-xl font-bold text-[hsl(var(--foreground))] flex items-center gap-2">
              <MdCloudUpload className="text-[#0088cc]" />
              Add Demo Data
            </h2>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">
              Creates realistic organizations, users, vessels, RFQs, vendor catalogue items, licenses,
              onboarding records, and global reference data. Safe to run multiple times — existing
              records are updated, not duplicated. All customer-portal demo data is scoped to{' '}
              <strong>Pacific Maritime Holdings Ltd</strong> and the seeded users below (log in on
              the customer portal with those emails after seeding).
            </p>
            <p className="text-xs text-amber-700 mt-3">
              First run or re-seed may take up to 2 minutes — keep this page open until you see the success summary.
            </p>
            <ul className="mt-3 text-sm text-[hsl(var(--muted-foreground))] list-disc list-inside space-y-1">
              <li>Tech &amp; Admin platform users (Euroasiann Group)</li>
              <li>Customer fleet — Pacific Maritime (3 vessels with full technical specs: engines, makers, models)</li>
              <li>OEM PMS library — 37 maker/model templates (Himsen H24/30, MAN, Wärtsilä, pumps, BWTP, etc.) with RH tasks applied per vessel equipment</li>
              <li>Contracted vendors — Maritime Engine Solutions, Gulf Ocean Marine, Western India Marine Electricals</li>
              <li>Contracted port agents — Singapore Port Services, Pacific Agency (customer-invited)</li>
              <li>Global vendors — Nordic Marine Supplies, Global Maritime Trading</li>
              <li>RFQs (sent, quoted, ordered) + quotations + purchase orders for Pacific Maritime</li>
              <li>Logistics / shipments — 4 demo tracks (port agent warehouse, alongside jetty, own warehouse, express)</li>
              <li>PMS — jobs, My PMS Jobs, and job cards under schedule PMS-DEMO-2026-001</li>
              <li>Global vessel database, brands, and categories</li>
              <li>Port infrastructure — Singapore, Rotterdam, Nhava Sheva (terminals, berths, tariffs, rules, warehouses, agents)</li>
              <li>Crew directory, certificates, contracts, payroll, travel &amp; HR employees (Pacific Maritime)</li>
              <li>Risk &amp; incident records, compliance &amp; certification items</li>
              <li>Classification societies (IACS register) + demo surveyors — linked to vessels &amp; PMS surveys</li>
              <li>Delivery claims (pending / accepted / rejected) tied to demo purchase orders &amp; RFQs</li>
            </ul>
          </div>
          <button
            type="button"
            onClick={() => seedMutation.mutate()}
            disabled={seedMutation.isPending}
            className="shrink-0 px-8 py-4 bg-[#0088cc] hover:bg-[#0077b3] disabled:opacity-60 text-white font-bold rounded-xl shadow-lg flex items-center gap-2"
          >
            <MdCloudUpload className="w-5 h-5" />
            {seedMutation.isPending ? 'Seeding...' : 'Add Demo Data'}
          </button>
        </div>

        {seedSummary && (
          <div className="mt-4 p-4 rounded-lg bg-emerald-50 border border-emerald-200 text-sm">
            <p className="font-semibold text-emerald-800 mb-1 flex items-center gap-1">
              <MdCheckCircle /> Last seed completed
            </p>
            <p className="text-emerald-700">
              {seedSummary.created.length} created · {seedSummary.updated.length} updated
            </p>
            {seedSummary.errors && seedSummary.errors.length > 0 && (
              <ul className="mt-2 text-amber-800 list-disc list-inside space-y-1">
                {seedSummary.errors.map((err) => (
                  <li key={err}>{err}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* Login credentials */}
      <div className="mb-8">
        <h2 className="text-xl font-bold text-[hsl(var(--foreground))] mb-4 flex items-center gap-2">
          <MdLogin className="text-[#0088cc]" />
          Portal Login Credentials
        </h2>
        {isLoading ? (
          <PageSkeleton variant="panel" />
        ) : (
          <div className="overflow-x-auto rounded-xl border border-[hsl(var(--border))]">
            <table className="w-full text-sm">
              <thead className="bg-[hsl(var(--muted))]/50">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Portal</th>
                  <th className="text-left px-4 py-3 font-semibold">Organization</th>
                  <th className="text-left px-4 py-3 font-semibold">User</th>
                  <th className="text-left px-4 py-3 font-semibold">Email</th>
                  <th className="text-left px-4 py-3 font-semibold">Password</th>
                  <th className="text-left px-4 py-3 font-semibold">Role</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[hsl(var(--border))]">
                {credentials.map((cred) => (
                  <tr key={cred.email} className="hover:bg-[hsl(var(--muted))]/20">
                    <td className="px-4 py-3 font-medium">{cred.portal}</td>
                    <td className="px-4 py-3 text-[hsl(var(--muted-foreground))]">{cred.organization}</td>
                    <td className="px-4 py-3">{cred.name}</td>
                    <td className="px-4 py-3 font-mono text-xs">{cred.email}</td>
                    <td className="px-4 py-3 font-mono text-xs">{cred.password}</td>
                    <td className="px-4 py-3">{cred.role}</td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => copyCredential(cred)}
                        className="p-2 rounded-lg hover:bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]"
                        title="Copy credentials"
                      >
                        {copiedEmail === cred.email ? (
                          <MdCheckCircle className="w-4 h-4 text-emerald-600" />
                        ) : (
                          <MdContentCopy className="w-4 h-4" />
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-xs text-[hsl(var(--muted-foreground))] mt-3">
          Run &quot;Add Demo Data&quot; first if accounts do not exist yet. All portals share the password{' '}
          <code className="font-mono bg-[hsl(var(--muted))] px-1 rounded">
            {credentials[0]?.password ?? 'J@yandra06'}
          </code>
          . Customer portal: use any Pacific Maritime row (e.g. procurement@pacificmaritime.com) — RFQs,
          orders, vendors, port agents, and PMS are linked to that organization.
        </p>
      </div>

      {/* Clean Data */}
      <div className="p-6 rounded-xl border border-red-500/30 bg-red-500/5 dark:bg-red-950/20">
        <h2 className="text-xl font-bold text-red-700 dark:text-red-400 mb-2 flex items-center gap-2">
          <MdDeleteForever />
          Clean Customer &amp; Vendor Data
        </h2>
        <p className="text-sm text-red-700/90 dark:text-red-300/90 mb-4 max-w-2xl">
          Permanently removes all customer and vendor organizations, users, RFQs, orders, vessels,
          and related data. Admin, Tech, and master reference data (ports, vessel database) are
          preserved.
        </p>

        {cleanStats && (
          <div className="mb-4 p-4 rounded-lg bg-[hsl(var(--card))] border border-red-500/20 text-sm">
            <p className="font-semibold mb-2">Last cleanup</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[hsl(var(--muted-foreground))]">
              <div>Orgs: {cleanStats.organizations}</div>
              <div>Users: {cleanStats.users}</div>
              <div>Roles: {cleanStats.roles}</div>
              <div>
                Onboardings: {cleanStats.customerOnboardings + cleanStats.vendorOnboardings}
              </div>
            </div>
          </div>
        )}

        <button
          type="button"
          onClick={() => setShowCleanModal(true)}
          disabled={cleanMutation.isPending}
          className="px-6 py-3 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-semibold rounded-lg flex items-center gap-2"
        >
          <MdDeleteForever className="w-5 h-5" />
          Clean Customer &amp; Vendor Data
        </button>
      </div>

      {showCleanModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-md">
          <div className="w-full max-w-md rounded-xl bg-[hsl(var(--card))] border shadow-2xl p-6 space-y-4">
            <h3 className="text-xl font-bold flex items-center gap-2">
              <MdWarning className="text-red-600" />
              Confirm data cleanup
            </h3>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              Type{' '}
              <code className="font-mono font-semibold text-red-600">{CLEAN_DATA_CONFIRM_PHRASE}</code>{' '}
              to permanently delete all customer and vendor portal data.
            </p>
            <input
              type="text"
              value={cleanConfirm}
              onChange={(e) => setCleanConfirm(e.target.value)}
              placeholder={CLEAN_DATA_CONFIRM_PHRASE}
              className="w-full px-4 py-3 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))]"
              autoComplete="off"
            />
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => {
                  setShowCleanModal(false);
                  setCleanConfirm('');
                }}
                className="px-4 py-2 rounded-lg border border-[hsl(var(--border))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => cleanMutation.mutate(cleanConfirm)}
                disabled={cleanMutation.isPending || cleanConfirm !== CLEAN_DATA_CONFIRM_PHRASE}
                className="px-4 py-2 rounded-lg bg-red-600 text-white font-semibold disabled:opacity-50"
              >
                {cleanMutation.isPending ? 'Cleaning...' : 'Confirm cleanup'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
